import { useTranslation } from "@/language"
/**
 * AdminCancelSessionModal
 * Confirmation modal for cancelling a coaching session booking (Admin view)
 *
 * Figma: https://www.figma.com/design/UlCtzXKPnAAma9QrgCWCge/Rituality---Web---Mob--Internal-?node-id=11169:37910
 */

import { XIcon } from "lucide-react"
import { useState } from "react"
import { CalendarFilledIcon, MoneyOutlinedIcon, PeopleOutlinedIcon } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Dialog, DialogClose, DialogContent } from "@/components/ui/dialog"
import { formatTime } from "@/features/booking/utils/format"
import { cn } from "@/lib"

export interface CancelSessionBookingDetails {
  id: string
  date: string
  startTime: string
  endTime: string
  user: {
    id: string
    name: string | null
    username: string | null
    email: string | null
  }
}

export interface AdminCancelSessionModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  booking: CancelSessionBookingDetails | null
  session: {
    id: string
    title: string
    participationFee: "Free" | "Paid"
    price: number | null
  }
  onCancel: (bookingId: string, message?: string) => void
  isCancelling?: boolean
}

function formatDateForDisplay(dateStr: string): string {
  const [year, month, day] = dateStr.split("-").map(Number)
  const d = new Date(year, month - 1, day)
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

export function AdminCancelSessionModal({
  open,
  onOpenChange,
  booking,
  session,
  onCancel,
  isCancelling = false,
}: AdminCancelSessionModalProps) {
  const { t } = useTranslation("coaching")
  const [message, setMessage] = useState("")

  if (!booking) return null

  const bookerName = booking.user.name || booking.user.username || "Participant"
  const bookerHandle = booking.user.username ? `@${booking.user.username}` : ""
  const bookerDisplay = bookerHandle ? `${bookerName} ${bookerHandle}` : bookerName

  const priceDisplay =
    session.participationFee === "Paid" && session.price ? `$${Math.round(session.price)}` : null

  function handleCancelSession() {
    if (!booking) return
    onCancel(booking.id, message || undefined)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="!max-w-[600px] border border-[#C86757]/50 bg-[#0B0605]/[0.95] p-0 text-connect-cream rounded-tr-[30px] rounded-bl-[30px]"
        overlayClass="bg-[rgba(0,0,0,0.9)] backdrop-blur-xl"
        showCloseButton={false}
      >
        <div className="flex flex-col gap-[22px] px-5 pt-5 pb-6">
          {/* Row: Title (left) + Close button (right) */}
          <div className="flex items-start justify-between">
            <div>
              <h2 className="font-montserrat text-[18px] font-semibold leading-[22px] text-[#F2F0EB]">
                Cancel Session?
              </h2>
              {/* Description */}
              <p className="font-montserrat text-[14px] font-normal leading-[25px] text-[#F2F0EB]/70 mt-3">
                The participant will be notified and the session will be cancelled.
              </p>
            </div>
            <DialogClose className="flex size-[40px] shrink-0 cursor-pointer items-center justify-center rounded-full border border-[#F2F0EB]/15 bg-[#F2F0EB]/5 text-[#F2F0EB] transition-opacity hover:opacity-80">
              <XIcon className="size-5 text-[#F2F0EB]" />
              <span className="sr-only">Close</span>
            </DialogClose>
          </div>

          {/* Booking Details Card — terracotta-tinted background, terracotta border */}
          <div className="flex flex-col gap-[5px] rounded-[15px] border border-[#C86757] bg-[#C86757]/[0.03] px-5 py-[15px]">
            {/* Participant row */}
            <div className="flex items-center gap-[10px]">
              <PeopleOutlinedIcon size={24} color="#C86757" />
              <span className="font-montserrat text-[13px] font-normal leading-[24px] text-[#C86757]">
                {bookerDisplay}
              </span>
            </div>
            {/* Date & Time row */}
            <div className="flex items-center gap-[10px]">
              <CalendarFilledIcon size={24} color="#C86757" />
              <span className="font-montserrat text-[13px] font-normal leading-[24px] text-[#C86757]">
                {formatDateForDisplay(booking.date)} – {formatTime(booking.startTime)} -{" "}
                {formatTime(booking.endTime)}
              </span>
            </div>
            {/* Revenue row (only for paid sessions) */}
            {priceDisplay && (
              <div className="flex items-center gap-[10px]">
                <MoneyOutlinedIcon size={24} color="#C86757" />
                <span className="font-montserrat text-[13px] font-normal leading-[24px] text-[#C86757]">
                  {priceDisplay} in expected revenue will be lost
                </span>
              </div>
            )}
          </div>

          {/* Optional message description */}
          <p className="font-montserrat text-[14px] font-normal leading-[25px] text-[#F2F0EB]/70">
            Add an optional message the participant will receive together with the cancellation
            notification.
          </p>

          {/* Message Textarea */}
          <div className="rounded-[15px] border border-white/15 bg-[#F2F0EB]/[0.00] px-5 py-5">
            <textarea
              id="cancel-message"
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder={t("addMessage")}
              className="min-h-[60px] w-full resize-none bg-transparent font-montserrat text-[14px] font-normal leading-[17px] text-[#F2F0EB]/90 placeholder:text-[#F2F0EB]/50 focus:outline-none"
            />
          </div>

          {/* Buttons — side by side, 20px gap */}
          <div className="flex items-center gap-[20px]">
            {/* Keep Session — outline button with offwhite border */}
            <Button
              onClick={() => onOpenChange(false)}
              disabled={isCancelling}
              className={cn(
                "h-[50px] flex-1 rounded-full text-[14px] font-semibold uppercase leading-[20px] transition-colors",
                "border border-[#F2F0EB] bg-transparent text-[#F2F0EB]",
                "hover:bg-[#F2F0EB]/10",
                "disabled:opacity-50",
              )}
            >
              Keep Session
            </Button>
            {/* Cancel Session — outline button with terracotta border */}
            <Button
              onClick={handleCancelSession}
              disabled={isCancelling}
              className={cn(
                "h-[50px] flex-1 rounded-full text-[14px] font-semibold uppercase leading-[20px] transition-colors",
                "border border-[#C86757] bg-transparent text-[#C86757]",
                "hover:bg-[#C86757]/10",
                "disabled:opacity-50",
              )}
            >
              {isCancelling ? "Cancelling..." : "Cancel SESSION"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
